import React, { useCallback, useEffect, useState } from 'react';
import Button from '../components/Button';
import Modal from '../components/Modal';
import {
    OrderUnderReview,
    organizerOrdersService,
    ReviewAction,
    ReviewResolutionError,
    ReviewType
} from '../services/organizerOrdersService';
import { PaymentProvider } from '../services/orderService';
import { DATE_TIME_SHORT, formatInstant } from '../utils/dates';

const REVIEW_TYPES: Record<ReviewType, { label: string; explanation: string }> = {
    LATE_PAYMENT: {
        label: 'Pago después de vencer la reserva',
        explanation: 'El pago se aprobó cuando ya había vencido el tiempo de la reserva, y sus boletas volvieron a estar a la venta. Si emites las boletas, se descuentan otra vez del aforo (puedes superarlo si ya se vendieron).'
    },
    DUPLICATE_PAYMENT: {
        label: 'Pago duplicado',
        explanation: 'El comprador pagó dos veces esta orden. Sus boletas ya se emitieron con el primer pago: devuélvele el pago adicional desde tu pasarela y marca la orden como resuelta.'
    },
    AMOUNT_MISMATCH: {
        label: 'El valor pagado no coincide',
        explanation: 'El valor o la moneda aprobados por la pasarela no coinciden con el total de la orden. Revisa el pago en tu pasarela: emite las boletas si lo aceptas, o reembolsa al comprador.'
    },
    PAYMENT_REVERSED: {
        label: 'Pago anulado o reembolsado',
        explanation: 'La pasarela informa que el pago fue anulado, reembolsado o desconocido después de emitir las boletas. Revisa el caso con el comprador; las boletas siguen siendo válidas.'
    },
    FULFILLMENT_FAILED: {
        label: 'No se pudieron emitir las boletas',
        explanation: 'El pago está aprobado, pero no logramos emitir las boletas automáticamente. Puedes intentarlo de nuevo o reembolsar al comprador.'
    }
};

const ACTIONS: Record<ReviewAction, { label: string; confirmTitle: string; confirmText: string; done: string }> = {
    ISSUE_TICKETS: {
        label: 'Emitir boletas',
        confirmTitle: 'Emitir las boletas',
        confirmText: 'Aceptas el pago de esta orden. Emitiremos las boletas y se las enviaremos al correo del comprador.',
        done: 'Las boletas se están emitiendo y llegarán al correo del comprador.'
    },
    MARK_REFUNDED: {
        label: 'Ya reembolsé al comprador',
        confirmTitle: 'Marcar como reembolsada',
        confirmText: 'Confirma que ya le devolviste el dinero al comprador desde tu pasarela. La orden quedará reembolsada, sin boletas, y sus entradas vuelven a estar disponibles.',
        done: 'La orden quedó reembolsada.'
    },
    MARK_RESOLVED: {
        label: 'Marcar como resuelta',
        confirmTitle: 'Marcar como resuelta',
        confirmText: 'La orden conserva sus boletas. Úsalo cuando ya resolviste el caso con el comprador.',
        done: 'La orden quedó resuelta.'
    }
};

const PROVIDER_LABELS: Record<PaymentProvider, string> = {
    BOLD: 'Bold',
    WOMPI: 'Wompi',
    MERCADOPAGO: 'Mercado Pago'
};

const formatCurrency = (amount: number, currency = 'COP') =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);

const formatDate = (dateString?: string) => formatInstant(dateString, DATE_TIME_SHORT, '—');

const Detail = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider mb-1">{label}</p>
        <div className="text-sm text-[var(--st-text-primary)] break-words">{children}</div>
    </div>
);

const OrderCard = ({ order, onAction }: { order: OrderUnderReview; onAction: (action: ReviewAction) => void }) => {
    const type = order.reviewType ? REVIEW_TYPES[order.reviewType] : undefined;
    return (
        <div className="st-card-static p-5">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">{order.eventName ?? 'Evento'}</h2>
                        <span className="st-badge st-badge-warning">{type?.label ?? 'Revisión manual'}</span>
                    </div>
                    <p className="text-sm text-[var(--st-text-secondary)]">
                        {type?.explanation ?? 'Esta orden necesita una revisión manual.'}
                    </p>
                </div>
                <p className="font-display text-xl font-bold text-[var(--st-text-primary)] whitespace-nowrap">
                    {formatCurrency(order.amount, order.currency)}
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <Detail label="Comprador">
                    {order.buyerEmail}
                    {order.buyerPhone && <><br />{order.buyerPhone}</>}
                </Detail>
                <Detail label="Boletas">
                    {order.items.map((item, index) => (
                        <span key={index} className="block">{item.stageName ?? 'Entrada'} × {item.quantity}</span>
                    ))}
                    <span className="block text-xs text-[var(--st-text-muted)]">{order.ticketsIssued ? 'Emitidas' : 'Sin emitir'}</span>
                </Detail>
                <Detail label="Pago">
                    {order.provider ? PROVIDER_LABELS[order.provider] : 'Sin pago asociado'}
                    {order.transactionId && <span className="block text-xs text-[var(--st-text-muted)]">{order.transactionId}</span>}
                </Detail>
                <Detail label="Fecha">
                    {formatDate(order.paidAt ?? order.createdAt)}
                    <span className="block text-xs text-[var(--st-text-muted)]">Ref. {order.reference}</span>
                </Detail>
            </div>

            {order.reviewReason && (
                <details className="mb-4 text-xs text-[var(--st-text-muted)]">
                    <summary className="cursor-pointer">Detalle técnico</summary>
                    <p className="mt-1 break-words">{order.reviewReason}</p>
                </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
                {order.allowedActions.map(action => (
                    <Button
                        key={action}
                        variant={action === 'MARK_REFUNDED' ? 'secondary' : 'primary'}
                        className={`st-btn ${action === 'MARK_REFUNDED' ? '' : 'st-btn-primary'} st-btn-md w-full sm:w-auto`}
                        onClick={() => onAction(action)}
                    >
                        {ACTIONS[action].label}
                    </Button>
                ))}
            </div>
        </div>
    );
};

export default function OrganizerOrdersReview() {
    const [orders, setOrders] = useState<OrderUnderReview[] | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [pending, setPending] = useState<{ order: OrderUnderReview; action: ReviewAction } | null>(null);
    const [isResolving, setIsResolving] = useState(false);
    const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

    const load = useCallback(async () => {
        try {
            setOrders(await organizerOrdersService.getOrdersUnderReview());
            setLoadError(false);
        } catch {
            setLoadError(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const confirm = async () => {
        if (!pending) return;
        setIsResolving(true);
        try {
            await organizerOrdersService.resolve(pending.order.orderId, pending.action);
            setOrders(current => (current ?? []).filter(order => order.orderId !== pending.order.orderId));
            setNotice({ kind: 'success', text: ACTIONS[pending.action].done });
        } catch (e) {
            const status = e instanceof ReviewResolutionError ? e.status : 0;
            setNotice({
                kind: 'error',
                text: status === 503
                    ? 'No pudimos devolver las boletas al inventario. Intenta de nuevo en unos segundos.'
                    : status === 404 || status === 409
                        ? 'La orden cambió mientras la revisabas. Actualizamos la lista.'
                        : 'No pudimos resolver la orden. Intenta de nuevo.'
            });
            if (status === 404 || status === 409) load();
        } finally {
            setIsResolving(false);
            setPending(null);
        }
    };

    return (
        <div className="st-page py-12">
            <div className="st-container">
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Órdenes en revisión</h1>
                    <p className="text-[var(--st-text-secondary)] max-w-3xl">
                        Pagos que necesitan tu decisión. El dinero de estas órdenes está en tu cuenta de la pasarela:
                        emite las boletas o reembolsa al comprador. SuperTicket no hace reembolsos.
                    </p>
                </div>

                {notice && (
                    <div className={`st-alert ${notice.kind === 'success' ? 'st-alert-success' : 'st-alert-error'} mb-6`}>
                        <div className="st-alert-content"><p className="st-alert-message">{notice.text}</p></div>
                    </div>
                )}

                {orders === null && !loadError && (
                    <div className="flex items-center justify-center min-h-[30vh]"><div className="st-spinner st-spinner-xl"></div></div>
                )}

                {orders === null && loadError && (
                    <div className="st-card-static p-8 text-center">
                        <p className="text-[var(--st-text-secondary)] mb-4">No pudimos cargar las órdenes en revisión.</p>
                        <Button className="st-btn st-btn-primary" onClick={load}>Intentar de Nuevo</Button>
                    </div>
                )}

                {orders !== null && orders.length === 0 && (
                    <div className="st-card-static p-10 text-center">
                        <h2 className="font-display text-xl font-bold text-[var(--st-text-primary)] mb-2">No tienes órdenes en revisión</h2>
                        <p className="text-[var(--st-text-secondary)]">Aquí aparecerán los pagos que necesiten tu decisión.</p>
                    </div>
                )}

                {orders !== null && orders.length > 0 && (
                    <div className="space-y-6">
                        {orders.map(order => (
                            <OrderCard key={order.orderId} order={order} onAction={action => setPending({ order, action })} />
                        ))}
                    </div>
                )}
            </div>

            <Modal
                isOpen={pending !== null}
                onClose={() => !isResolving && setPending(null)}
                title={pending ? ACTIONS[pending.action].confirmTitle : ''}
                className="max-w-md"
            >
                {pending && (
                    <>
                        <p className="text-[var(--st-text-secondary)] mb-2">{ACTIONS[pending.action].confirmText}</p>
                        <p className="text-sm text-[var(--st-text-muted)] mb-6">
                            {pending.order.eventName ?? 'Evento'} · {pending.order.buyerEmail} · {formatCurrency(pending.order.amount, pending.order.currency)}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-end">
                            <Button variant="ghost" className="st-btn st-btn-ghost" onClick={() => setPending(null)} disabled={isResolving}>
                                Cancelar
                            </Button>
                            <Button className="st-btn st-btn-primary" onClick={confirm} disabled={isResolving}>
                                {isResolving ? 'Procesando...' : 'Confirmar'}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}
